import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { BehaviorSubject, finalize, Observable, pairwise, shareReplay } from 'rxjs';
import { DatePickerType } from '../is-date-time-picker/is-date-time-picker.component';
import { ISAPI, MVFilter, MVListingConfigs, MVOrdering, MVPagination } from '../models/api';
import { AlignType, ColumnType, FilterOptionsType, SortDirection } from '../models/generic';
import { filterBetweendDates, filterByDate, filterContainsByValue, sortByColumn } from './is-table.functions';
import { ButtonType, ColumnTypeConfig, ISChipSetData, ISChipSetDatasource, ISColumn, ISColumnToShow, ISLabelOptions, ISSelectedRow, ISTableDatasource, LinkType } from './is-table';
import { NgxSpinnerService } from 'ngx-spinner';
import { ISDialogService } from '../is-dialog/is-dialog.service';
import { Router } from '@angular/router';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { RequestReplayService } from '../services/requestReplay.service';
import { SpinnerService } from '../services/spinner.service';
import { safeStringify } from '../functions/safeStringify';

@Component({
    selector: 'is-table',
    templateUrl: './is-table.component.html',
    styleUrls: ['./is-table.component.scss']
})
export class ISTableComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() DataSource$: BehaviorSubject<ISTableDatasource> = new BehaviorSubject<ISTableDatasource>(new ISTableDatasource([], [])); // Used to store the table data
    @Input() showSpinner: boolean = true; // Show Spinner
    @Input() fullPageSpinner: boolean = false; // Show Spinner on fullscreen
    @Input() name: string = '';
    @Input() paginationPosition: 'top' | 'bottom' = 'bottom';
    @Input() chipSetDataSource: ISChipSetDatasource[] = [];
    @Input() useRequestReplay: boolean = false; // Determines if the requests made to the API are saved and their values replayed next time the table is loaded

    @Input() componentId?: string = undefined;
    @Output() componentIdChange = new EventEmitter<string>();

    @Input() customEmptyMessage: any;
    @Input() headerColor: string = '#4CCC8B';
    @Input() urlsToPersistDataSource: string[] = [];

    @Input() queryable: boolean = false;
    @Input() spinnerType: string = 'ball-pulse';

    @Output() onReceiveDataFromAPI: EventEmitter<any> = new EventEmitter<any>(); // Used to emit when the datasource receives the data from the api
    @Output() onCleanFilters: EventEmitter<any> = new EventEmitter<any>(); // Used to emit when the the table filters are cleared
    @Output() onActionBtnClick: EventEmitter<any> = new EventEmitter<any>(); // Used to emit the action button click event
    @Output() onSelectRow: EventEmitter<any> = new EventEmitter<any>(); // Used to emit the action button click event
    @Output() onSelectRows: EventEmitter<ISSelectedRow[]> = new EventEmitter<ISSelectedRow[]>(); // Used to emit the action button click event
    @Output() onRowsToKeepChange: EventEmitter<any> = new EventEmitter<any>(); // Used to emit the action button click event
    @Output() onChipSetClear: EventEmitter<any> = new EventEmitter<any>();
    @Output() onLinkOptionsClick: EventEmitter<any> = new EventEmitter<any>();


    menuPosition: ConnectedPosition[] = [{
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top'
    }];

    tableKey: string;

    ratings = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

    dateRangeType = DatePickerType.dateRange;
    dateType = DatePickerType.date;

    errorOnDataRetrieve: boolean = false;
    originBodyValues: any[] = []; // Used to store the original values of the table
    totalRecordsFromDB: number = 0;
    filtersForm: FormGroup | undefined; // Used to store the filters form in a form group
    showLoader: boolean = false; // Used to show the loader

    _unsubscribe: any[] = []; // Used to store the subscriptions
    _linkType = LinkType;

    showPagination: boolean = true; // Used to show the pagination
    selected: Date | null | undefined;
    reorderMenu: boolean = false; // Indicates if the reorder menu is visible or not

    allRowsSelected: boolean = false;
    selectedRows: ISSelectedRow[] = []; //Array with all selected rows

    previousUrl!: string;

    tableDetailsChanged: boolean = false;
    tableDetails: TableDetails[] = [];
    originalTableDetailsStringified: string = '';
    chipSetData: ISChipSetData[] = [];

    countries = [
        { code: 'PT', name: 'Portugal', flag: '/assets/images/flags/portugal.png', flagSquare: '/assets/images/flags/portugal-square.png' },
        { code: 'ES', name: 'Espanha', flag: '/assets/images/flags/espanha.png', flagSquare: '/assets/images/flags/espanha-square.png' }
    ];

    constructor(
        private _spinnerService: SpinnerService,
        @Inject('MV_SPINNER_TEMPLATE') public spinnerTemplate: string,
        private _httpClient: HttpClient,
        private _cd: ChangeDetectorRef,
        private _spinner: NgxSpinnerService,
        private _dialogService: ISDialogService,
        private _router: Router,
        private _paginationService: MatPaginatorIntl,
        private _translate: TranslateService,
        private _requestReplayService: RequestReplayService
    ) {
        this.tableKey = 'tableDetails' + (!String.isNullOrUndefinedOrWhitespace(this.name) ? '-' + this.name : '') + '-' + (this._router.url.replace('/', '_'));
    }

    ngOnInit(): void {
        this.previousUrl = localStorage.getItem('lastRoute') ?? '';
        this.showLoader = this._spinnerService.getInComponentSpinnerStatus(this.tableKey);
        this.DataSource$.value.isFetching = this._spinnerService.getInComponentSpinnerStatus(this.tableKey);

        const currentRoute = this._router.url;
        const storageKey = `tableFilters-${currentRoute}`;

        if (this.urlsToPersistDataSource.length > 0) {
            const previousUrlAffectsTable = this.urlsToPersistDataSource.some(path => this.previousUrl.includes(path));

            const existingDataSource = localStorage.getItem(storageKey);

            if (existingDataSource) {
                try {
                    const parsedDataSource = JSON.parse(existingDataSource);
                    this.DataSource$.next(parsedDataSource);
                } catch (error) {
                    console.error('Failed to parse stored data source:', error);
                }
            } else {
                localStorage.setItem(storageKey, JSON.stringify(this.DataSource$.value));
            }

            if (!previousUrlAffectsTable) {
                this.DataSource$.value.columns.forEach(column => {
                    column.filterOptions.value = '';
                });

                localStorage.setItem(storageKey, JSON.stringify(this.DataSource$.value));
            }
        }

        this.originBodyValues = this.DataSource$.value.body ?? [];
        // if (this.originBodyValues.length > 0) {
        //     this.showPagination = false;
        // }

        this.buildFiltersForm();

        if (this.originBodyValues.length > 0) {
            this.addSelectedRowProperty();
        }

        this.tableDetails = this.getTableDetails();

        this.DataSource$.pipe(pairwise()).subscribe(([prevValue, currentValue]) => {
            this.ngOnChanges({ 'DataSource$': { "previousValue": prevValue, "currentValue": currentValue, "firstChange": false, "isFirstChange": () => false } });
        });

        if (!String.isNullOrUndefinedOrWhitespace(this.DataSource$.value.apiUrl)) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl);
            this.totalRecordsFromDB = this.DataSource$.value.totalItems;
        }

        this.setupChipSetData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let dataSourceChanges = changes['DataSource$'];

        if (dataSourceChanges) {
            if (dataSourceChanges.currentValue instanceof ISTableDatasource) {
                if (safeStringify(dataSourceChanges.previousValue) != safeStringify(dataSourceChanges.currentValue)) {
                    this.tableDetails = this.getTableDetails();
                    this.originalTableDetailsStringified = safeStringify(this.tableDetails);
                }
            }

            else if (dataSourceChanges.currentValue instanceof BehaviorSubject) {
                if (safeStringify(dataSourceChanges.previousValue?.value) != safeStringify(dataSourceChanges.currentValue?.value)) {
                    this.tableDetails = this.getTableDetails();
                    this.originalTableDetailsStringified = safeStringify(this.tableDetails);
                }
            }

            else {
                if (safeStringify(dataSourceChanges.previousValue) != safeStringify(dataSourceChanges.currentValue)) {
                    this.tableDetails = this.getTableDetails();
                    this.originalTableDetailsStringified = safeStringify(this.tableDetails);
                }
            }
        }

        let nameChanges = changes['name'];

        if (nameChanges) {
            this.tableKey = 'tableDetails' + (!String.isNullOrUndefinedOrWhitespace(nameChanges.currentValue) ? '_' + nameChanges.currentValue : '') + (this._router.url.replace('/', '_'));
        }
    }

    ngAfterViewInit(): void {
        if (!this.componentId) {
            this.componentId = crypto.randomUUID();
            this.componentIdChange.emit(this.componentId);
        }
    }

    /**
     * @description Set the configuration of the table
     * @param  {ISTableDatasource} dataSource The configuration of the table
     */
    setDataSourceBehaviorSubject(dataSource: ISTableDatasource) {
        this.DataSource$.next(dataSource);
    }

    /**
     * @description Build the filters form with the controls
     */
    buildFiltersForm() {
        if (!this.filtersForm) this.filtersForm = new FormGroup({});

        this.dataSourceColumnsToShow().forEach((column: ISColumnToShow) => {
            if (column.type.type === 'date') {
                this.filtersForm?.addControl(column.identifier, new FormControl<Date | string>(''));
            }
            else if (column.type.type == ColumnType.multiselect) {
                this.filtersForm?.addControl(column.identifier, new FormControl<any>([]));
            }
            else {
                this.filtersForm?.addControl(column.identifier, new FormControl<any>(''));
            }
        });
        this.addFormSubscriptions();
    }

    filterSearch() {
        //if (this.totalRecordsFromDB >= this.DataSource$.value.TotalItems)
        if (this.DataSource$.value.apiUrl) { //&& (this.totalRecordsFromDB > this.DataSource$.value.Pagination.PageSize)
            this.DataSource$.value.pagination.page = 1;
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl, true);
        } //else {
        //    this.filterTableData();
        // }
    }

    cleanForm() {
        this.DataSource$.value.columns.forEach((column: ISColumn) => {
            this.filtersForm?.controls[column.identifier].setValue(null, { emitEvent: false });
        });

        this.onCleanFilters.emit();

        if (this.DataSource$.value.apiUrl) { //&& (this.DataSource$.value.TotalItems != this.totalRecordsFromDB)) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl);
        }
    }

    /**
     * @description Add the form subscriptions
     */
    addFormSubscriptions() {
        this.dataSourceColumnsToShow().forEach((column: ISColumnToShow) => {
            //Run every time the value of the control changes
            if (column.filterOptions.value != null && column.filterOptions.value != '') {
                this.filtersForm?.controls[column.identifier].setValue(column.filterOptions.value);
            }
            this._unsubscribe.push(this.filtersForm?.controls[column.identifier].valueChanges.subscribe((filter) => {
                column.filterOptions.value = filter;

                if (this.urlsToPersistDataSource.length > 0) {
                    const currentRoute = this._router.url;
                    const storageKey = `tableFilters-${currentRoute}`;
                    localStorage.setItem(storageKey, JSON.stringify(this.DataSource$.value));
                }

                if (column.filterOptions.type) {
                    if (column.filterOptions.type == FilterOptionsType.dateRange) {
                        if (filter?.start != null && filter?.start != undefined) {
                            column.filterOptions.value = filter.start.toISOString() + '|' + filter.end.toISOString();
                        } else {
                            column.filterOptions.value = null;
                        }

                    } else {
                        column.filterOptions.value = filter;
                    }

                    if (column.filterOptions.type != FilterOptionsType.text && filter?.value != null) {
                        this.getDataFromTheAPI();
                        this.DataSource$.value.pagination.page = 1;
                    }

                } else {
                    if (column.type.type == ColumnType.dateRange) {
                        if (filter?.start != null && filter?.start != undefined) {
                            column.filterOptions.value = filter.start.toISOString() + '|' + filter.end.toISOString();
                        } else {
                            column.filterOptions.value = null;
                        }

                    } else {
                        column.filterOptions.value = filter;
                    }

                    if (column.type.type != ColumnType.text && filter?.value != null) {
                        this.getDataFromTheAPI();
                        this.DataSource$.value.pagination.page = 1;
                    }
                }
            }));
        });
    }

    getDataFromTheAPI(APIUrl?: string, forceAPIRequest: boolean = false) {
        let requestObservable: Observable<any> | undefined;

        this.showLoader = true;
        this.DataSource$.value.isFetching = true;
        const pagination = this.DataSource$.value.pagination;
        let table: ISAPI | MVListingConfigs;

        if (this.showSpinner) {
            if (this.fullPageSpinner) {
                this._spinnerService.showFullscreenSpinner();
            } else {
                this._spinnerService.showInComponentSpinner(this.tableKey);
            }

        }

        this.setupChipSetData();

        // Build filter object based on queryable flag
        if (this.queryable) {
            // NEW: MVListingConfigs
            const mvFilters: MVFilter[] = [];

            this.dataSourceColumnsToShow().forEach((column: ISColumnToShow) => {
                let filterVal = column.filterOptions?.value;

                if (column.type.type === ColumnType.multiselect && Array.isArray(filterVal)) {
                    filterVal = filterVal.join('|');
                }

                if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                    mvFilters.push(new MVFilter(
                        column.filterOptions.searchField ?? column.identifier,
                        filterVal,
                        column.filterOptions?.type || 'text',
                        column.filterOptions?.operator || 'contains',
                        column.filterOptions?.navigation
                    ));
                }
            });

            this.DataSource$.value.headerFilters?.forEach((column: ISColumn) => {
                const filterVal = column.filterOptions?.value;
                if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                    mvFilters.push(new MVFilter(
                        column.filterOptions.searchField ?? column.identifier,
                        filterVal,
                        column.filterOptions?.type || 'text',
                        column.filterOptions?.operator || 'contains',
                        column.filterOptions?.navigation
                    ));
                }
            });

            const orderingColumn = this.DataSource$.value.columns.find(c => c.direction && (c.direction == SortDirection.asc || c.direction == SortDirection.desc));
            const ordering = orderingColumn
                ? new MVOrdering(
                    orderingColumn.filterOptions.searchField ?? orderingColumn.identifier,
                    orderingColumn.direction,
                    orderingColumn.filterOptions?.navigation
                )
                : null;

            table = new MVListingConfigs(
                mvFilters,
                ordering,
                new MVPagination(pagination.page, pagination.pageSize)
            );

        } else {
            // OLD: ISAPI
            table = new ISAPI([], pagination);

            this.dataSourceColumnsToShow().forEach((column: ISColumnToShow) => {
                let filterVal = column.filterOptions?.value;
                if (column.type.type === ColumnType.multiselect && Array.isArray(filterVal)) {
                    filterVal = filterVal.join('|');
                }

                (table as ISAPI).filters.push({
                    identifier: column.identifier,
                    value: filterVal,
                    type: column.filterOptions?.type,
                    operator: column.filterOptions?.operator,
                    orderType: column.direction,
                    navigation: column.filterOptions?.navigation,
                    searchField: column.filterOptions?.searchField,
                    alternativeIdentifier: column.alternativeIdentifier ?? column.filterOptions.alternativeIdentifier
                });
            });

            this.DataSource$.value.headerFilters?.forEach((column: ISColumn) => {
                (table as ISAPI).filters.push({
                    identifier: column.identifier,
                    value: column.filterOptions?.value,
                    type: column.filterOptions?.type,
                    operator: column.filterOptions?.operator,
                    orderType: column.direction,
                    navigation: column.filterOptions?.navigation,
                    searchField: column.filterOptions?.searchField,
                    alternativeIdentifier: column.alternativeIdentifier ?? column.filterOptions.alternativeIdentifier
                });
            });
        }

        // Request replay logic
        if (this.useRequestReplay && !forceAPIRequest) {
            requestObservable = this._requestReplayService.getRequestReplay(this.tableKey, this.componentId);

            if (requestObservable == undefined) {
                requestObservable = this._httpClient.post((APIUrl ?? this.DataSource$.value.apiUrl) ?? '', table).pipe(shareReplay(), finalize(() => {
                    if (this.fullPageSpinner) {
                        this._spinnerService.hideFullscreenSpinner();
                    } else {
                        this._spinnerService.hideInComponentSpinner(this.tableKey);
                        this.showLoader = false;
                        this.DataSource$.value.isFetching = false;
                    }
                }));
                this._requestReplayService.saveRequestReplay(this.tableKey, requestObservable, this.componentId);
            }
        } else {
            requestObservable = this._httpClient.post((APIUrl ?? this.DataSource$.value.apiUrl) ?? '', table).pipe(finalize(() => {
                if (this.fullPageSpinner) {
                    this._spinnerService.hideFullscreenSpinner();
                } else {
                    this._spinnerService.hideInComponentSpinner(this.tableKey);
                    this.showLoader = false;
                    this.DataSource$.value.isFetching = false;
                }
            }));
        }

        this._unsubscribe.push(
            requestObservable.subscribe({
                next: (res: any) => {
                    if (res) {
                        this.errorOnDataRetrieve = false;

                        this.DataSource$.value.body = [];
                        res.data.forEach((item: any) => {
                            this.DataSource$.value.body?.push(item);
                        });
                        this.DataSource$.value.body = res.data;

                        this.addSelectedRowProperty();
                        this.originBodyValues = res.data;
                        this.DataSource$.value.totalItems = res.totalListCount;
                        this.showLoader = false;
                        this.DataSource$.value.isFetching = false;

                        if (this.totalRecordsFromDB < this.DataSource$.value.totalItems) {
                            this.totalRecordsFromDB = this.DataSource$.value.totalItems;
                        }

                        if (this.DataSource$.value.totalItems <= this.DataSource$.value.pagination?.pageSize) {
                            this.showPagination = false;
                        }
                        else {
                            this.showPagination = true;
                        }

                        this.onReceiveDataFromAPI.emit();
                    }
                },
                error: (err: Error) => {
                    this.errorOnDataRetrieve = true;
                }
            })
        );
    }

    /**
     * @description On change page
     * @param  {any} event - Event (Ex:  { "previousPageIndex": 3, "pageIndex": 4, "pageSize": 10, "length": 50 })
     */
    handlePageEvent(event: any) {
        this.DataSource$.value.pagination.page = event.pageIndex + 1;
        this.DataSource$.value.pagination.pageSize = event.pageSize;
        if (this.DataSource$.value.apiUrl) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl);
        } else {
            this.filterTableData();
        }
    }

    /**
     * @description Gets the Label text and label style based on row option
     */
    getLabel(value: any, options: ISLabelOptions[], optionToGet: any): string {
        const option = options.find(x => x.value === value);

        if (option) {
            switch (optionToGet) {
                case 'Label':
                    return option?.label;
                case 'Style':
                    return 'badge-' + option?.style;
                default:
                    return '  -  ';
            }
        } else {
            switch (optionToGet) {
                case 'Label':
                    return '-';
                case 'Style':
                    return 'badge-light-danger';
                default:
                    return '-';
            }
        }
        return '';
    }

    getColumnIcon(item: any, column: ISColumn) {
        let columnConditional = column.conditionalFunction;

        if (item != null) {
            if (columnConditional != null) {
                return columnConditional(item);
            }
        }
    }

    columnHasTooltip(column: ISColumn, item?: any): boolean {
        const text = this.getColumnTooltipText(column, item);
        return !!text && String(text).trim().length > 0;
    }

    getColumnTooltipText(column: ISColumn, item: any): string {
        // 1) dynamic function
        if (column.tooltipFn) {
            try {
                return column.tooltipFn(item) ?? '';
            } catch {
                return '';
            }
        }

        // 2) read from a field
        if (column.tooltipField) {
            const v = item?.[column.tooltipField];
            return v == null ? '' : String(v);
        }

        // 3) old behavior: alternativeIdentifier tooltip
        if (column.alternativeIdentifier) {
            const v = item?.[column.alternativeIdentifier];
            return v == null ? '' : String(v);
        }

        // 4) old behavior: tooltipText (translation key)
        return column.tooltipText ?? '';
    }


    getOptionIcon(item: string, column: ISColumn) {
        if (item == null) item = "null";
        let icon = column.options.find(x => x.value == item.toString())?.icon;
        return icon ?? null;
    }

    getOptionStyle(item: string, column: ISColumn) {
        if (item == null) item = "null";
        let icon = column.options.find(x => x.value == item.toString())?.style;
        return icon ?? null;
    }

    getOptionLabel(item: string, column: ISColumn) {
        if (item == null) item = "null";
        let label = column.options.find(x => x.value == item.toString())?.label;
        return label ?? null;
    }

    getBooleanOptionLabel(item: boolean | any, column: ISColumn) {
        if (item == null) item = "null";
        let label = column.options.find(x => x.value == item)?.label;
        return label ?? null;
    }

    /**
     * @description Filter the table by the column
     */
    filterTableData() {
        let tempArrayFilter = this.originBodyValues;
        if (this.DataSource$.value.columns.find((column: ISColumn) => column.direction !== SortDirection.none)) {
            this.resetOrder(this.DataSource$.value.columns.find((column: ISColumn) => column.direction !== SortDirection.none));
        }

        this.DataSource$.value.columns.forEach((column: ISColumn) => {
            if (column.filterOptions?.value && column.filterOptions?.value !== '') {
                if (column.filterOptions?.type === FilterOptionsType.dateRange) {
                    let dates: string[] = JSON.parse(column.filterOptions?.value);
                    let start = dates[0];
                    let end = dates[1];

                    tempArrayFilter = filterBetweendDates<any>(tempArrayFilter, new Date(start), new Date(end), column.identifier);

                } else if (column.filterOptions?.type === FilterOptionsType.date) {
                    tempArrayFilter = filterByDate<any>(tempArrayFilter, new Date(column.filterOptions?.value), column.identifier);

                } else {
                    tempArrayFilter = filterContainsByValue<any>(tempArrayFilter, column.filterOptions.value, column.identifier);
                }
            }
        });
        this.DataSource$.value.body = tempArrayFilter;
    }

    onDatePickerSelected(event: any, column: ISColumn) {
        this.filtersForm?.controls[column.identifier].setValue(event);
        if (this.DataSource$.value.apiUrl) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl);
        } else {
            this.filterTableData();
        }
    }

    /**
     * @description Output the data of the row when the action button is clicked
     * @param  {string} action - Action of the button
     * @param  {any} row - Row data
     */
    onClickButtonAction(action: ButtonType, row: any) {
        this.onActionBtnClick.emit({ action, row });
    }

    /**
     * @description Order the table by the column
     * @param  {ISColumn} column - Column to be ordered
     */
    onClickOrderBy(column: ISColumn) {
        //Reset the direction of the other columns
        this.resetOrder(column);

        //Change the direction of the column
        switch (column.direction) {
            case 'asc':
                column.direction = SortDirection.desc;
                break;
            case 'desc':
                column.direction = SortDirection.none;
                break;
            default:
                column.direction = SortDirection.asc;
                break;
        }

        // local data only or
        // number of records is <= table page size
        if (!this.DataSource$.value.apiUrl || (this.DataSource$.value.totalItems <= this.DataSource$.value.pagination.pageSize)) {
            this.DataSource$.value.body = sortByColumn<any>(this.DataSource$.value.body ?? [], column);

        } else if (this.DataSource$.value.apiUrl) {
            this.getDataFromTheAPI();

        } else if (column.direction === SortDirection.none) {
            this.DataSource$.value.body = this.originBodyValues;
            this.filterTableData();
        }
    }

    /**
     * @description Reset the order of the columns
     * @param  {ISColumn} exceptionColumn? - Column to be excluded from the reset
     */
    resetOrder(exceptionColumn?: ISColumn) {
        this.DataSource$.value.columns.forEach((column: ISColumn) => {
            if (exceptionColumn && column.identifier !== exceptionColumn.identifier) {
                column.direction = SortDirection.none;
            }
        });
    }

    /**
     * @description On row select
     * @param  {number} index - Row Index
     * @param  {any} rowData - Row Data
     * @returns {void}
     */
    onSelectedRows(index: number, rowData: any): void {
        const rowIndex = this.selectedRows.findIndex(x => x.id === rowData.id);

        //Só permite selecionar 1 row
        if (!this.DataSource$.value.selectableRows.selectMultipleRows) {
            if (rowIndex !== -1) { //Se existir significa que estamos a desmarcar a row
                this.DataSource$.value.body?.forEach(element => {
                    element.selected = false;
                });
                this.selectedRows = [];
            } else { //Se ainda não existir significa que a row não está selecionada e estamos a marcar a row
                this.DataSource$.value.body?.forEach(element => {
                    element.selected = false;
                });
                this.selectedRows = [];
                this.DataSource$.value.body![index].selected = true;
                this.selectedRows.push({ id: rowData.id, index: index, rowData: rowData });
            }
        } else { //Permite selecionar várias
            if (rowIndex !== -1) {
                this.selectedRows.splice(rowIndex, 1);
                this.DataSource$.value.body![index].selected = false;
                let rowToKeepIndex = this.DataSource$.value.selectableRows.rowsToKeep?.findIndex(x => x.id === this.DataSource$.value.body![index].id);
                if (rowToKeepIndex && rowToKeepIndex > -1 || rowToKeepIndex === 0) {
                    this.DataSource$.value.selectableRows.rowsToKeep?.splice(rowToKeepIndex, 1);
                    this.onRowsToKeepChange.emit({ rowsToKeep: this.DataSource$.value.selectableRows.rowsToKeep, selectedRows: this.selectedRows });
                }
            } else {
                this.selectedRows.push({ id: rowData.id, index: index, rowData: rowData });
                this.DataSource$.value.body![index].selected = true;
                let rowToKeepIndex = this.DataSource$.value.selectableRows.rowsToKeep?.findIndex(x => x.id === this.DataSource$.value.body![index].id);
                if (rowToKeepIndex && rowToKeepIndex === -1 || rowToKeepIndex === 0) {
                    this.DataSource$.value.selectableRows.rowsToKeep?.push({ id: this.DataSource$.value.body![index].id });
                    this.onRowsToKeepChange.emit({ rowsToKeep: this.DataSource$.value.selectableRows.rowsToKeep, selectedRows: this.selectedRows });
                }
                this.selectedRows.sort((a, b) => a.index - b.index);
            }
        }
        this._cd.detectChanges();
        this.onSelectRow.emit(rowData);
        this.onSelectRows.emit(this.selectedRows);
    }

    /**
     * @description Adds the property "selected" to each data row, so we can select/deselect/pre-select rows
     * @returns {void}
     */
    addSelectedRowProperty(): void {
        const selected = { selected: false };
        let that = this;
        //For each data row
        this.DataSource$.value.body?.forEach(function (element, index) {
            //If the selected property exists
            if (element.selected !== undefined) {
                //If the property exists and its selected already we push that row to the selected rows array
                if (element.selected === true) {
                    that.selectedRows.push({ id: element.id, index: index, rowData: element });

                    // suggestion
                    // let elementIndex = that.selectedRows.findIndex(x => x.id == element.id)

                    // if (elementIndex == -1) {
                    //     that.selectedRows.push({ id: element.id, index: index, rowData: element });
                    // }
                }

            } else { //Else we add the property
                let idToKeep = that.DataSource$.value.selectableRows.rowsToKeep?.find(x => x.id == element.id);

                if (idToKeep) {
                    selected.selected = true;
                    that.selectedRows.push({ id: element.id, index: index, rowData: element });
                    Object.assign(element, selected);

                } else {
                    let elementIndex = that.selectedRows.findIndex(x => x.id == element.id);

                    if (elementIndex != -1) {
                        that.selectedRows.splice(elementIndex, 1);
                    }

                    selected.selected = false;
                    Object.assign(element, selected);
                }
            }
        });
    }

    /**
     * @description Select/Deselect all rows
     * @returns {void}
     */
    selectAllRows(): void {
        this.allRowsSelected = !this.allRowsSelected;
        let that = this;
        if (this.allRowsSelected) {
            this.selectedRows = [];
            this.DataSource$.value.body?.forEach(function (element, index) {
                element.selected = true;
                that.selectedRows.push({ id: element.id, index: index, rowData: element });

                let rowToKeepIndex = that.DataSource$.value.selectableRows.rowsToKeep?.findIndex(x => x.id == element.id);
                if (rowToKeepIndex && rowToKeepIndex == -1 || rowToKeepIndex == 0) {
                    that.DataSource$.value.selectableRows.rowsToKeep?.push({ id: element.id });
                    that.onRowsToKeepChange.emit({ rowsToKeep: that.DataSource$.value.selectableRows.rowsToKeep, selectedRows: that.selectedRows });
                }
            });
        } else {
            this.selectedRows = [];
            this.DataSource$.value.body?.forEach(function (element, index) {
                element.selected = false;
                let rowToKeepIndex = that.DataSource$.value.selectableRows.rowsToKeep?.findIndex(x => x.id == element.id);
                if (rowToKeepIndex && rowToKeepIndex > -1 || rowToKeepIndex == 0) {
                    that.DataSource$.value.selectableRows.rowsToKeep?.splice(rowToKeepIndex, 1);
                    that.onRowsToKeepChange.emit({ rowsToKeep: that.DataSource$.value.selectableRows.rowsToKeep, selectedRows: that.selectedRows });
                }
            });
        }
    }

    /**
     * Função para selecionar uma linha através de um index ou id (para ser usada por componentes externos).
     * @param index Index da linha a selecionar
     * @param id Id da linha a selecionar (tem prioridade no caso de serem enviados ambos os parâmetros com valor)
     */
    public selectRow(index?: number, id?: string) {
        let selectedObj: any = undefined;
        let selectedObjIndex: any = undefined;

        if (id != null) {
            selectedObjIndex = this.DataSource$.value.body?.findIndex(el => el.id == id);
            selectedObj = this.DataSource$.value.body?.at(selectedObjIndex);
        }
        if (index != null) {
            selectedObj = this.DataSource$.value.body?.at(index);
        }

        if (selectedObj) {
            selectedObj.selected = true;
            this.selectedRows.push({ id: selectedObj.id, index: index ?? selectedObjIndex, rowData: selectedObj });
            this.onSelectRows.emit(this.selectedRows);
            this._cd.detectChanges();
        }
    }

    /**
     * Função para desselecionar uma linha através de um index ou id (para ser usada por componentes externos).
     * @param index Index da linha a selecionar
     * @param id Id da linha a selecionar (tem prioridade no caso de serem enviados ambos os parâmetros com valor)
     */
    public desselectRow(index?: number, id?: string) {
        let selectedObj: any = undefined;

        if (id != null) {
            selectedObj = this.DataSource$.value.body?.find(el => el.id == id);
        }
        if (index != null) {
            selectedObj = this.DataSource$.value.body?.at(index);
        }

        if (selectedObj) {
            selectedObj.selected = false;
            let selectedRowIndex = this.selectedRows.findIndex(row => row.id == selectedObj.id);
            this.selectedRows.splice(selectedRowIndex, 1);
            this.onSelectRows.emit(this.selectedRows);
            this._cd.detectChanges();
        }


    }

    generateUniqueId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    }

    /**
     * @description On Destroy component destroy the subscriptions
     */
    ngOnDestroy(): void {
        this._unsubscribe.forEach(sub => sub.unsubscribe());
    }

    getRecordCountries(recordValue?: string) {
        return recordValue?.split(';');
    }

    /**
    * @description - Com base no código do pais devolve o "path" da imagem da bandeira desse país
    * @param {string} code Código do país (ex: PT)
    * @returns {void}
    */
    getCountryFlag(code: string): string {
        return this.countries.find(x => x.code === code)?.flag ?? '';
    }

    /**
     * Trata de aplicar as funções dos links:
     * - Dialog - prepara a data a ser enviada para o dialog e evoca-o
     * - Url - prepara o url e navega para o mesmo
     * - Route - prepara a rota (routing do Angular) e navega para a mesma
     * @param column Coluna do tipo 'link'
     * @param item Objeto da linha selecionada com os dados do DTO
     */
    handleLinkEvent(column: ISColumn, item: any, event: MouseEvent) {
        if (column.linkOptions) {

            //Output click event
            this.onLinkOptionsClick.emit({ column, item });
            //Im passing the column because there can bu multiple link columns in the same table and the parent component needs to know which one was clicked

            //If LinkOptions is custom the behaviour is handled by the parent component
            if (column.linkOptions.type == LinkType.custom) {
                return;
            }

            let linkOptions = { ...column.linkOptions };

            // Se for usado o botão do meio do rato abre sempre numa nova página
            if (event.button == 1) {
                linkOptions.openNewPage = true;
                event.preventDefault();
            }

            // Caso seja dialog
            if (linkOptions?.type == LinkType.dialog) {
                // Guarda uma cópia da configuração do dialog (para o original não ser alterado)
                let dialogConfig = { ...linkOptions.dialogConfig };

                // Se tiver os dados necessários (Componente e configuração)
                if (linkOptions.dialogComponent && linkOptions.dialogConfig) {

                    // Se tiver dados para substituir
                    if (linkOptions.recordIdentifiers && linkOptions.recordIdentifiers.length > 0) {
                        // Guarda a string do objeto de dados a enviar para o dialog
                        let dialogDataString = safeStringify(dialogConfig);
                        // Substitui as strings de substituição ($!{index}) pelos valores de substituição do DTO
                        linkOptions.recordIdentifiers.forEach((identifier, index) => {
                            let strToReplace = '$!' + index;
                            dialogDataString = dialogDataString.replaceAll(strToReplace, item[identifier]);
                        });
                        // Volta a converter a string num objeto já com os objetos substituídos
                        dialogConfig = JSON.parse(dialogDataString);
                    }

                    if (Object.hasOwn(linkOptions.dialogConfig.data, 'tableRef')) {
                        dialogConfig.data.tableRef = this;
                    }

                    // Evoca o dialog
                    this._dialogService.open(linkOptions.dialogComponent, dialogConfig);
                }
            }
            else {
                // Guarda a rota de maneira a não alterar a original
                let routeUrl = linkOptions?.url ?? "";

                // Se tiver dados para substituir
                if (linkOptions.recordIdentifiers && linkOptions.recordIdentifiers.length > 0) {
                    // Substitui as strings de substituição ($!{index}) pelos valores de substituição do DTO
                    linkOptions.recordIdentifiers.forEach((identifier, index) => {
                        let strToReplace = '$!' + index;
                        routeUrl = routeUrl.replaceAll(strToReplace, item[identifier]);
                    });
                }

                // Caso seja do tipo URL
                if (linkOptions?.type == LinkType.url) {
                    if (linkOptions.openNewPage) {
                        window.open(routeUrl, '_blank');
                    }
                    window.location.href = routeUrl;
                }

                // Caso seja do tipo Route
                if (linkOptions?.type == LinkType.route) {
                    if (linkOptions.openNewPage) {
                        let serializedRoute = this._router.serializeUrl(this._router.createUrlTree([routeUrl]));
                        window.open(routeUrl, '_blank');
                        return;
                    }
                    this._router.navigate([routeUrl]);
                }
            }
        }
    }

    /**
     * Executa a troca de posição das colunas
     * @param event evento que indica o index anterior e atual da coluna
     */
    drop(event: any) {
        // Copia e executa as trocas numa cópia do objeto original para as alterações serem aplicadas no final de uma só vez
        let auxObject: any = this.tableDetails.at(event.previousIndex)!;

        this.tableDetails[event.previousIndex] = this.tableDetails.at(event.currentIndex)!;
        this.tableDetails[event.currentIndex] = auxObject;

        this.tableDetailsChanged = JSON.stringify(this.tableDetails) != this.originalTableDetailsStringified;
    }

    setupChipSetData() {
        if (this.chipSetDataSource.length > 0) {
            this.chipSetDataSource.forEach((datasource: ISChipSetDatasource) => {
                let matchingColumn = this.DataSource$.value.headerFilters?.find((col: ISColumn) => col.identifier == datasource.filterName) ?? this.DataSource$.value.columns?.find((col: ISColumn) => col.identifier == datasource.filterName);

                let matchingLabelColumn: ISColumn | undefined = undefined;

                if (datasource.labelColumn) {
                    matchingLabelColumn = this.DataSource$.value.columns.find((col: ISColumn) => col.identifier == datasource.labelColumn);
                } else {
                    matchingLabelColumn = matchingColumn;
                }

                if (matchingColumn != null) {
                    let matchingChipSetData = this.chipSetData.find((chipSet: ISChipSetData) => chipSet.filterName == matchingColumn!.identifier);
                    if (!matchingChipSetData) {
                        this.chipSetData.push(new ISChipSetData(datasource.displayName, matchingColumn.identifier, ''));
                        matchingChipSetData = this.chipSetData.at(-1);
                    }

                    if (datasource.displayString) {
                        matchingChipSetData!.displayString = datasource.displayString;
                    } else {
                        if (!String.isNullOrUndefinedOrWhitespace(matchingColumn.filterOptions.value)) {
                            matchingChipSetData!.displayString = matchingColumn.filterOptions.value.split('|').map((chip: string) => {
                                if (matchingLabelColumn && matchingLabelColumn.labelOptions.length > 0) {
                                    return matchingLabelColumn.labelOptions.find((label: ISLabelOptions) => label.value == chip)?.label;
                                } else {
                                    return chip;
                                }
                            }).join(', ');
                        } else {
                            matchingChipSetData!.displayString = '';
                        }

                    }

                } else {
                    let matchingChipSetData = this.chipSetData.find((chipSet: ISChipSetData) => chipSet.filterName == datasource.filterName);
                    if (!matchingChipSetData) {
                        this.chipSetData.push(new ISChipSetData(datasource.displayName, datasource.filterName, ''));
                        matchingChipSetData = this.chipSetData.at(-1);
                    }

                    matchingChipSetData!.displayString = '';
                }
            });

        }
    }

    get chipsetDataToShow(): ISChipSetData[] {
        return this.chipSetData.filter((chipset: ISChipSetData) => !String.isNullOrUndefinedOrWhitespace(chipset.displayString));
    }
    //#region Table details

    /**
     * Grabs the table details from the localStorage if this table hasnt been saved yet creates a new details object
     * @returns Object with details of the table
     */
    getTableDetails(): TableDetails[] {
        let detailsValueString = localStorage.getItem(this.tableKey);
        if (!String.isNullOrUndefinedOrWhitespace(detailsValueString)) {
            let detailsValue = JSON.parse(detailsValueString!);

            return detailsValue;
        } else {
            return this.DataSource$.value.columns.reduce<TableDetails[]>((accumulator: TableDetails[], col: ISColumn, index: number) => {
                accumulator[index] = { id: col.identifier, show: col.show, title: col.title };
                return accumulator;
            }, []);
        }
    }

    /**
     * Saves the table configuration to the local storage (order and visibility)
     */
    saveTableDetails() {
        let value = JSON.stringify(this.tableDetails);

        localStorage.setItem(this.tableKey, value);

        this.originalTableDetailsStringified = value;
        this.tableDetailsChanged = false;
    }

    /**
     * Hides/Shows the selected column
     * @param columnDetail Column to hide/show
     */
    visibilityToggle(columnDetail: any) {
        columnDetail.show = !columnDetail.show;

        this.tableDetailsChanged = JSON.stringify(this.tableDetails) != this.originalTableDetailsStringified;
    }

    /**
     * Devolve só as colunas visiveis
     */
    dataSourceColumnsToShow(): ISColumnToShow[] {
        return this.DataSource$.value.columns.map(col => ({
            ...col,
            type: this.normalizeColumnType(col.type)
        })).filter(col => this.tableDetails.find((detail: any) => detail.id == col.identifier)?.show).sort((current, next) => this.tableDetails.findIndex((detail: any) => detail.id == current.identifier) - this.tableDetails.findIndex((detail: any) => detail.id == next.identifier));
    }

    trackByColumn(index: number, col: ISColumnToShow) {
        return col.identifier;
    }

    private normalizeColumnType(columnType: ColumnType | ColumnTypeConfig): ColumnTypeConfig {
        if (typeof columnType === 'string') {
            return { type: columnType };
        }

        return columnType;
    }

    getAlignmentClass(col: ISColumn) {
        switch (col.align) {
            case AlignType.left: return 'text-start';
            case AlignType.center: return 'text-center';
            case AlignType.right: return 'text-end';
            default: return 'text-start';
        }
    }
    //#endregion

    columnIsEmpty(value: string) {
        return String.isNullOrUndefinedOrWhitespace(value);
    }
}

class TableDetails {
    constructor(
        public id: string,
        public show: boolean,
        public title: string
    ) { }
}
